import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Link2, Pencil, Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { Item, ItemMapping } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { GroupPicker } from '@/components/GroupPicker'
import { SearchableGroupPicker } from '@/components/SearchableGroupPicker'
import { ItemPicker } from '@/components/ItemPicker'
import { SearchBox } from '@/components/SearchBox'
import { Field, inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'
import { useListNavigation } from '@/lib/useListNavigation'
import { useFormFieldNavigation } from '@/lib/useFormFieldNavigation'

const MAPPING_FORM_FIELDS = ['group', 'source_code', 'source_name', 'item'] as const

interface MappingForm {
  id?: number
  group: number | null
  group_label: string
  source_code: string
  source_name: string
  item: number | null
  item_label: string
}

const blankForm: MappingForm = {
  group: null, group_label: '',
  source_code: '', source_name: '',
  item: null, item_label: '',
}

export function ItemMappingsTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [groupId, setGroupId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState<MappingForm | null>(null)

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['item-mappings', 'quote', groupId, search],
    queryFn: () => {
      const params = new URLSearchParams()
      params.set('kind', 'quote')   // 견적서 매핑만 (발주서 매핑 제외)
      if (groupId) params.set('group_id', String(groupId))
      if (search) params.set('q', search)
      return api<ItemMapping[]>(`/api/item-mappings?${params}`)
    },
  })

  const updateItem = useMutation({
    mutationFn: ({ id, item_id }: { id: number; item_id: number }) =>
      api<ItemMapping>(`/api/item-mappings/${id}/`, {
        method: 'PATCH', body: { item: item_id },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-mappings'] })
      push('매핑 변경됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 5000),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/item-mappings/${id}/`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-mappings'] })
      push('삭제됨', 'success')
    },
    onError: (e) => push(`삭제 실패: ${e.message}`, 'error', 5000),
  })

  const save = useMutation({
    mutationFn: (f: MappingForm) => {
      const body = {
        source_code: f.source_code.trim(),
        source_name: f.source_name.trim(),
        item: f.item,
        group: f.group,
        partner: null,
      }
      return f.id
        ? api<ItemMapping>(`/api/item-mappings/${f.id}/`, { method: 'PATCH', body })
        : api<ItemMapping>('/api/item-mappings/', { method: 'POST', body })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-mappings'] })
      setEdit(null); push('저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })

  function openAdd() {
    setEdit({ ...blankForm })
  }

  function openEdit(r: ItemMapping) {
    setEdit({
      id: r.id,
      group: r.group, group_label: r.group_name ?? '',
      source_code: r.source_code || '',
      source_name: r.source_name || '',
      item: r.item, item_label: r.item_name ? `${r.item_code} ${r.item_name}` : '',
    })
  }

  const { formRef, onKeyDown: onFormKeyDown } = useFormFieldNavigation(MAPPING_FORM_FIELDS)

  const { tbodyRef, onKeyDown: onListKeyDown } = useListNavigation<ItemMapping>({
    items: rows,
    disabled: !!edit,
    onEnter: (r) => openEdit(r),
    onDelete: (r) => {
      void confirm(`'${r.source_name}' 매핑 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id))
    },
  })

  const canSubmit = edit
    ? !!edit.item && !!(edit.source_code.trim() || edit.source_name.trim()) && !!edit.group
    : false

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200/60 mb-3">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm">
        {/* 상위 거래처 필터 — 타이핑 검색 */}
        <SearchableGroupPicker
          value={groupId}
          onChange={(g) => setGroupId(g?.id ?? null)}
          placeholder="상위 거래처 필터"
          className="w-64"
        />

        <SearchBox value={search} onChange={setSearch}
                   placeholder="원본코드/품명/품목 검색" className="flex-1 min-w-[200px]" />
        <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
        <button onClick={openAdd}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5"/> 매핑 추가
        </button>
      </div>
      </div>

      {rows.length >= 1000 && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <span>
            표시 상한(<b>1,000</b>건)에 도달했습니다. <b>상위 거래처</b>를 선택하거나
            <b> 검색</b>으로 좁혀서 보세요. (거래처별로 보면 전체가 나옵니다.)
          </span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium w-52">상위 거래처</th>
              <th className="px-3 py-2.5 text-left font-medium w-32">원본코드</th>
              <th className="px-3 py-2.5 text-left font-medium">원본품명</th>
              <th className="px-3 py-2.5 text-center font-medium w-8">→</th>
              <th className="px-3 py-2.5 text-left font-medium w-64">품목 (편집 가능)</th>
              <th className="px-3 py-2.5 text-right font-medium w-24"></th>
            </tr>
          </thead>
          <tbody ref={tbodyRef} onKeyDown={onListKeyDown}>
            {rows.map((r, idx) => (
              <tr key={r.id} data-idx={idx} tabIndex={idx === 0 ? 0 : -1}
                  className="border-t border-slate-100 hover:bg-slate-50 focus:bg-blue-50 focus:outline-none">
                <td className="px-3 py-1.5 text-xs font-medium text-slate-800">
                  {r.group_name ?? '—'}
                </td>
                <td className="px-3 py-1.5 font-mono text-xs tabular-nums text-slate-600">{r.source_code || '—'}</td>
                <td className="px-3 py-1.5 text-slate-800">{r.source_name}</td>
                <td className="px-3 py-1.5 text-center text-slate-300"><Link2 className="w-3.5 h-3.5 inline"/></td>
                <td className="px-3 py-1.5">
                  <ItemPicker value={r.item}
                              initialLabel={r.item_name ? `${r.item_code} ${r.item_name}` : ''}
                              onChange={(it: Item) => updateItem.mutate({ id: r.id, item_id: it.id })}/>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)}
                          title="상세 편집"
                          className="text-blue-600 hover:text-blue-800 mr-2">
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  <button onClick={() => { void confirm(`'${r.source_name}' 매핑 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && !isFetching && (
              <tr>
                <td colSpan={6} className={cn('text-center py-10 text-sm text-slate-400')}>
                  매핑이 없습니다. 우측 상단 "매핑 추가"로 시작하거나 견적서 변환에서 자동 생성됩니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? '코드 매핑 수정' : '코드 매핑 추가'} size="xl">
        {edit && (
          <form ref={formRef} onKeyDown={onFormKeyDown}
                onSubmit={(e) => { e.preventDefault(); if (canSubmit) save.mutate(edit) }}
                className="space-y-3">
            <Field label="상위 거래처 *" full>
              <div data-form-field="group">
                <GroupPicker
                  value={edit.group}
                  onChange={(g) => setEdit({ ...edit, group: g.id, group_label: `${g.name} · ${g.member_count}개` })}
                  placeholder={edit.group_label || '상위 거래처 선택'}
                />
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                이 상위 거래처의 자식 매출처 전부에 적용됩니다.
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="원본코드">
                <input data-form-field="source_code" value={edit.source_code}
                       onChange={(e) => setEdit({ ...edit, source_code: e.target.value })}
                       placeholder="견적서의 품목코드"
                       className={inputCls + ' font-mono tabular-nums'}/>
              </Field>
              <Field label="원본품명">
                <input data-form-field="source_name" value={edit.source_name}
                       onChange={(e) => setEdit({ ...edit, source_name: e.target.value })}
                       placeholder="견적서의 품명"
                       className={inputCls}/>
              </Field>
            </div>
            <div className="text-xs text-slate-400 -mt-1">
              원본코드/품명 중 최소 하나 필수. 코드가 있으면 코드 매칭 우선.
            </div>

            <Field label="매핑할 품목 *" full>
              <div data-form-field="item" className="border border-slate-300 rounded-lg">
                <ItemPicker
                  value={edit.item}
                  onChange={(it) => setEdit({ ...edit, item: it.id, item_label: `${it.code} ${it.name}` })}
                  placeholder={edit.item_label || '품목 선택'}
                />
              </div>
            </Field>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)}
                      className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit"
                      disabled={!canSubmit || save.isPending}
                      className={cn(
                        'px-4 py-2 text-sm font-semibold rounded-lg',
                        (!canSubmit || save.isPending)
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white',
                      )}>
                {save.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
